import type { TextLayerData } from "@/lib/text-layer";
import { checksTerminal, type Checks } from "@/lib/upload-report";

export type RenderStatus = "ready" | "rendering" | "failed";
export type RenderAssetTier = "thumb" | "page" | "text";

export interface RenderTier {
  dpi: number;
  tile?: number;
}

export interface RenderPage {
  pageId: string;
  n: number;
  wPt: number;
  hPt: number;
  rotation: 0 | 90 | 180 | 270;
  thumb: Record<string, unknown>;
  page: Record<string, unknown>;
  zoom: Record<string, unknown>;
  ready: { thumb: boolean; page: boolean; zoom: boolean; text: boolean };
}

export interface RenderManifest {
  versionId: string;
  gen: number;
  renderer: string;
  status: RenderStatus;
  tiers: { thumb: RenderTier; page: RenderTier; zoom: RenderTier };
  pages: RenderPage[];
  failed: number[];
  checks?: Checks;
}

export interface ManifestResponse {
  manifest?: RenderManifest;
  etag: string | null;
  notModified: boolean;
}

export class RenderManifestError extends Error {
  constructor(readonly status: number) {
    super(`render manifest request failed with status ${status}`);
    this.name = "RenderManifestError";
  }
}

export const MANIFEST_POLL_INTERVAL_MS = 2_000;
export const MAX_RETRY_DELAY_MS = 30_000;

export function renderAssetUrl(versionId: string, gen: number, n: number, tier: RenderAssetTier): string {
  const filename = tier === "text" ? "text.json" : `${tier}.webp`;
  return `/api/versions/${encodeURIComponent(versionId)}/render/${gen}/pages/${n}/${filename}`;
}

function retryDelay(response: Response, attempt: number): number {
  const retryAfter = Number(response.headers.get("retry-after"));
  const minimum = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1_000 : 2_000;
  return Math.min(minimum * 2 ** attempt, MAX_RETRY_DELAY_MS);
}

function pause(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("The page changed.", "AbortError"));
      return;
    }
    const abort = () => {
      clearTimeout(timeoutId);
      signal.removeEventListener("abort", abort);
      reject(new DOMException("The page changed.", "AbortError"));
    };
    const timeoutId = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, milliseconds);
    /* `abort` closes over `timeoutId` before its declaration, which is safe because the
     * listener is registered only after the timer exists, so the closure cannot run in
     * the temporal dead zone. Written this way to satisfy prefer-const. */
    signal.addEventListener("abort", abort, { once: true });
  });
}

export async function fetchRenderAsset<T>(
  versionId: string,
  gen: number,
  n: number,
  tier: RenderAssetTier,
  signal: AbortSignal,
  read: (response: Response) => Promise<T>,
  allowMissing = false,
): Promise<T | null> {
  let attempt = 0;
  const url = renderAssetUrl(versionId, gen, n, tier);
  while (true) {
    const response = await fetch(url, { credentials: "same-origin", signal });
    if (response.status === 202) {
      await pause(retryDelay(response, attempt), signal);
      attempt += 1;
      continue;
    }
    if (allowMissing && response.status === 404) return null;
    if (!response.ok) throw new RenderManifestError(response.status);
    return read(response);
  }
}

export function fetchRenderText(versionId: string, gen: number, n: number, signal: AbortSignal): Promise<TextLayerData | null> {
  return fetchRenderAsset(versionId, gen, n, "text", signal, async (response) => response.json() as Promise<TextLayerData>, true);
}

export async function fetchRenderManifest(versionId: string, etag?: string | null): Promise<ManifestResponse> {
  const headers = etag ? { "if-none-match": etag } : undefined;
  const response = await fetch(`/api/versions/${encodeURIComponent(versionId)}/render`, {
    credentials: "same-origin",
    headers,
  });
  if (response.status === 304) return { etag: response.headers.get("etag") ?? etag ?? null, notModified: true };
  if (!response.ok) throw new RenderManifestError(response.status);
  return {
    manifest: await response.json() as RenderManifest,
    etag: response.headers.get("etag"),
    notModified: false,
  };
}

export interface ManifestPollerOptions {
  onChange: (manifest: RenderManifest) => void;
  onError: (error: unknown) => void;
  intervalMs?: number;
  document?: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;
}

/** Fetch now, then refresh rendering manifests until a terminal state, pausing while hidden. */
export function startRenderManifestPolling(versionId: string, options: ManifestPollerOptions): () => void {
  let stopped = false;
  let terminal = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let etag: string | null = null;
  // Consecutive failures, reset by any success, so a poll that recovers goes
  // straight back to the two-second cadence instead of staying backed off.
  let failures = 0;
  const intervalMs = options.intervalMs ?? MANIFEST_POLL_INTERVAL_MS;
  const documentRef = options.document ?? (typeof document === "undefined" ? undefined : document);
  const isHidden = () => documentRef?.visibilityState === "hidden";

  const clearScheduledPoll = () => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    timeoutId = undefined;
  };
  const stop = () => {
    stopped = true;
    clearScheduledPoll();
    documentRef?.removeEventListener("visibilitychange", visibilityChange);
  };
  const visibilityChange = () => {
    if (isHidden()) return clearScheduledPoll();
    if (!stopped && !terminal) void poll();
  };
  const poll = async () => {
    if (stopped || terminal || isHidden()) return;
    try {
      const result = await fetchRenderManifest(versionId, etag);
      etag = result.etag;
      failures = 0;
      if (result.manifest) {
        options.onChange(result.manifest);
        if (result.manifest.status !== "rendering" && checksTerminal(result.manifest.checks)) {
          terminal = true;
          return;
        }
      }
      if (!stopped && !terminal && !isHidden()) timeoutId = setTimeout(poll, intervalMs);
    } catch (error) {
      /* Report the error AND keep polling. This used to report and stop, so one
       * dropped request, a sleeping laptop's first fetch, a redeploy, a phone
       * changing network, ended the poll for good and left the proof reading
       * "Preparing page 52 of 52" until the reviewer reloaded the page. The
       * render itself was fine and finishing on the server; only the thing
       * watching it had given up, which is the worst shape of failure here
       * because nothing on screen says so.
       *
       * Backing off rather than retrying every two seconds, and capped by the
       * same MAX_RETRY_DELAY_MS the asset fetch uses (:47), so a server that is
       * down does not get a request every two seconds from every open proof. */
      if (stopped) return;
      options.onError(error);
      failures += 1;
      if (!terminal && !isHidden()) timeoutId = setTimeout(poll, Math.min(intervalMs * 2 ** failures, MAX_RETRY_DELAY_MS));
    }
  };

  documentRef?.addEventListener("visibilitychange", visibilityChange);
  void poll();
  return stop;
}
